/**
 * OwnerSidebarNav — Permanent left sidebar for the Business Owner
 * dashboard on desktop (`lg:` and up). Groups every existing tab under
 * the four sections from the design brief.
 *
 * Drives the same `activeTab` state the existing tab bar uses, so it's
 * a second view of the same navigation — nothing regresses on mobile /
 * tablet where the horizontal tab bar remains.
 */
import { Link } from "react-router-dom";
import {
  Home, Package, ShoppingBag, CreditCard, Sparkles, Star, Megaphone,
  Instagram, BarChart3, Users, MessageSquare, User as UserIcon,
  BadgeCheck, Settings, Share2, QrCode, ArrowLeft, Crown,
} from "lucide-react";

const GROUPS = [
  {
    label: null,
    items: [
      { label: "Overview", tab: "overview", icon: Home },
    ],
  },
  {
    label: "Store",
    items: [
      { label: "Products", tab: "products", icon: Package },
      { label: "Orders", tab: "orders", icon: ShoppingBag },
      { label: "Payouts", tab: "payouts", icon: CreditCard },
    ],
  },
  {
    label: "Promote",
    items: [
      { label: "Blinterest Drops", tab: "products", icon: Sparkles, focus: "drop" },
      { label: "Featured Placement", tab: "featured", icon: Star },
      { label: "Banner Ads", tab: "featured", icon: Megaphone, focus: "banner" },
      { label: "Instagram Sync", tab: "profile", icon: Instagram, focus: "instagram" },
    ],
  },
  {
    label: "Insights",
    items: [
      { label: "Analytics", tab: "analytics", icon: BarChart3 },
      { label: "Customers / Leads", tab: "customers", icon: Users },
      { label: "Reviews", tab: "reviews", icon: MessageSquare },
    ],
  },
  {
    label: "Business",
    items: [
      { label: "Profile", tab: "profile", icon: UserIcon },
      { label: "Verification", tab: "profile", icon: BadgeCheck, focus: "verify" },
      { label: "Settings", tab: "settings", icon: Settings },
      { label: "Referrals", tab: "referrals", icon: Share2 },
      { label: "My QR Code", tab: "qr", icon: QrCode },
    ],
  },
];

export default function OwnerSidebarNav({ activeTab, focus, onSelect, businessName }) {
  return (
    <aside
      className="hidden lg:flex flex-col w-60 flex-shrink-0 min-h-[calc(100vh-5rem)] bg-tbc-navy text-white pb-4"
      data-testid="owner-sidebar"
    >
      <div className="px-5 pt-6 pb-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-tbc-gold" />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-tbc-gold font-semibold">Business Owner</p>
            <p className="font-heading text-white text-base leading-tight truncate">{businessName || "Dashboard"}</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 py-3 space-y-1 overflow-y-auto">
        {GROUPS.map((g) => (
          <div key={g.label || "primary"} className="mb-2">
            {g.label && (
              <p className="px-5 pt-3 pb-1 text-[9px] uppercase tracking-widest text-white/40 font-semibold">{g.label}</p>
            )}
            {g.items.map((item) => {
              const isActive = activeTab === item.tab && (item.focus ? focus === item.focus : !focus);
              const Icon = item.icon;
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => onSelect(item.tab, item.focus || null)}
                  className={`w-full flex items-center gap-3 px-5 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-tbc-gold/10 text-tbc-gold border-l-4 border-tbc-gold pl-4"
                      : "text-white/80 hover:text-white hover:bg-white/5 border-l-4 border-transparent"
                  }`}
                  data-testid={`owner-sidebar-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="px-3 border-t border-white/10 pt-3">
        <Link
          to="/"
          className="w-full flex items-center gap-3 px-2 py-2.5 text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 rounded-md transition-colors"
          data-testid="owner-sidebar-back-to-website"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Website</span>
        </Link>
      </div>
    </aside>
  );
}
